/**
 * AGENT PROFILE (2026-11-01).
 *
 * The only thing an agent may change about themselves is the name shown on their paying account — the code,
 * the rate and the cap are OGESTYLE's decision and are displayed read-only here so there is no ambiguity
 * about who set them.
 */
import React, { useEffect, useState } from "react";
import axios from "axios";
import { getApiBaseUrl, getAuthHeaders } from "../../utils/apiHelpers";
import {
  AgentPageState,
  formatDate,
  styles,
  useAgentResource,
} from "./agentShared";

export default function AgentProfile() {
  const { loading, error, unavailable, data, reload } = useAgentResource(
    "/api/platform-plans/referral/summary",
  );
  const [displayName, setDisplayName] = useState("");
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [saveError, setSaveError] = useState("");

  useEffect(() => {
    if (data?.agent) setDisplayName(data.agent.displayName || "");
  }, [data]);

  const save = async (event) => {
    event.preventDefault();
    setSaving(true);
    setSaved(false);
    setSaveError("");
    try {
      const headers = getAuthHeaders();
      if (!headers) return;
      await axios.patch(
        `${getApiBaseUrl()}/api/platform-plans/referral/profile`,
        { displayName },
        { headers },
      );
      setSaved(true);
      await reload();
      window.setTimeout(() => setSaved(false), 2500);
    } catch (err) {
      setSaveError(
        err?.response?.data?.message || "Could not save your name. Please try again.",
      );
    } finally {
      setSaving(false);
    }
  };

  return (
    <AgentPageState
      loading={loading}
      error={error}
      unavailable={unavailable}
      reload={reload}
    >
      <div style={styles.page}>
        <h1 style={styles.h1}>Profile</h1>
        <p style={{ ...styles.muted, marginTop: 0 }}>
          How you appear to OGESTYLE and on your commission statements.
        </p>

        <div style={styles.card}>
          <h2 style={styles.h2}>Your details</h2>
          <form onSubmit={save}>
            <label
              htmlFor="agent-display-name"
              style={{ ...styles.statLabel, marginBottom: "8px" }}
            >
              Display name
            </label>
            <div style={{ display: "flex", flexWrap: "wrap", gap: "10px" }}>
              <input
                id="agent-display-name"
                type="text"
                value={displayName}
                maxLength={120}
                onChange={(event) => setDisplayName(event.target.value)}
                style={{ ...styles.input, flex: "1 1 320px" }}
                placeholder="Your name"
              />
              <button type="submit" style={styles.btn} disabled={saving}>
                {saving ? "Saving…" : "Save"}
              </button>
            </div>
            {saved ? (
              <p style={{ ...styles.muted, color: "#065f46", marginBottom: 0 }}>
                Saved.
              </p>
            ) : null}
            {saveError ? <div style={styles.err}>{saveError}</div> : null}
          </form>
        </div>

        <div style={styles.card}>
          <h2 style={styles.h2}>Your agent account</h2>
          <table style={styles.table}>
            <tbody>
              <tr>
                <td style={{ ...styles.td, ...styles.muted }}>Referral code</td>
                <td style={styles.td}>
                  <strong style={{ fontFamily: "monospace", letterSpacing: "0.06em" }}>
                    {data?.agent?.code || "—"}
                  </strong>
                </td>
              </tr>
              <tr>
                <td style={{ ...styles.td, ...styles.muted }}>Commission rate</td>
                <td style={styles.td}>{data?.agent?.commissionPercent ?? "—"}%</td>
              </tr>
              <tr>
                <td style={{ ...styles.td, ...styles.muted }}>Commission period</td>
                <td style={styles.td}>
                  {data?.agent?.commissionCapMonths
                    ? `${data.agent.commissionCapMonths} months per referred location`
                    : "Open-ended"}
                </td>
              </tr>
              <tr>
                <td style={{ ...styles.td, ...styles.muted }}>Account status</td>
                <td style={styles.td}>{data?.agent?.status || "—"}</td>
              </tr>
              <tr>
                <td style={{ ...styles.td, ...styles.muted }}>Referred</td>
                <td style={styles.td}>
                  {data?.totals?.referrals || 0} business
                  {data?.totals?.referrals === 1 ? "" : "es"}
                </td>
              </tr>
            </tbody>
          </table>
          <p style={{ ...styles.muted, marginBottom: 0 }}>
            The rate and the cap are set by OGESTYLE and are fixed for you when your
            account is approved, so a later change to the platform rate does not
            rewrite what you agreed to.
          </p>
        </div>

        <div style={styles.card}>
          <h2 style={styles.h2}>Payout account</h2>
          {data?.connect?.payoutsEnabled ? (
            <p style={{ marginTop: 0, marginBottom: 0 }}>
              Connected
              {data?.connect?.bank?.last4
                ? ` · bank account ending ${data.connect.bank.last4}`
                : ""}
              . Manage it from Payouts.
            </p>
          ) : data?.connect?.connected ? (
            <p style={{ marginTop: 0, marginBottom: 0 }}>
              Started but not finished — Stripe still needs your bank and identity
              details. Finish it on the Payouts page so commission can reach your
              bank.
            </p>
          ) : (
            <p style={{ marginTop: 0, marginBottom: 0 }}>
              No payout account yet — add one on the Payouts page so commission can
              reach your bank.
            </p>
          )}
          {data?.recentCommissions?.[0]?.at ? (
            <p style={{ ...styles.muted, marginBottom: 0 }}>
              Last commission {formatDate(data.recentCommissions[0].at)}.
            </p>
          ) : null}
        </div>
      </div>
    </AgentPageState>
  );
}
